import { Link } from 'react-router-dom'
import { Check } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { cn } from '@/lib/utils'

/**
 * Getting started step configuration
 */
interface Step {
  number: number
  title: string
  description: string
  href: string
  isComplete?: boolean
}

const steps: Step[] = [
  {
    number: 1,
    title: 'Define Attributes',
    description:
      'Start by defining the attribute schemas that will be used in your policies.',
    href: '/pac/attributes',
  },
  {
    number: 2,
    title: 'Create Policies',
    description:
      'Use poli-x AI or the manual builder to create access control policies.',
    href: '/pac/policies/create',
  },
  {
    number: 3,
    title: 'Test & Deploy',
    description:
      'Test your policies and deploy them to enforce access control decisions.',
    href: '/pac/policies',
  },
]

/**
 * Single step card component wrapped in a link
 */
function StepCard({ step }: { step: Step }) {
  return (
    <Link to={step.href} className="block">
      <div
        className={cn(
          'rounded-lg border p-4 transition-[background-color,border-color,box-shadow] duration-200 cursor-pointer',
          'hover:shadow-md hover:-translate-y-0.5 hover:border-primary/30',
          step.isComplete && 'bg-primary/5 border-primary/20'
        )}
      >
        <div
          className={cn(
            'mb-2 flex h-10 w-10 items-center justify-center rounded-full text-lg font-bold',
            step.isComplete
              ? 'bg-primary text-primary-foreground'
              : 'bg-primary/10 text-primary'
          )}
        >
          {step.isComplete ? (
            <Check className="h-5 w-5" />
          ) : (
            step.number
          )}
        </div>
        <h3 className="mb-1 font-medium">{step.title}</h3>
        <p className="text-sm text-muted-foreground">{step.description}</p>
      </div>
    </Link>
  )
}

interface GettingStartedProps {
  attributeCount?: number
  policyCount?: number
}

/**
 * Getting Started guide component
 * Shows a 3-step onboarding flow with completion status
 */
export function GettingStarted({
  attributeCount = 0,
  policyCount = 0,
}: GettingStartedProps) {
  // Determine completion status based on counts
  const stepsWithStatus = steps.map((step, index) => {
    let isComplete = false
    if (index === 0 && attributeCount > 0) isComplete = true
    if (index === 1 && policyCount > 0) isComplete = true
    // Step 3 is considered complete when both policies and attributes exist
    if (index === 2 && attributeCount > 0 && policyCount > 0) isComplete = true
    return { ...step, isComplete }
  })

  const completedCount = stepsWithStatus.filter((s) => s.isComplete).length

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Getting Started</span>
          {completedCount > 0 && (
            <span className="text-sm font-normal text-muted-foreground">
              {completedCount}/{steps.length} complete
            </span>
          )}
        </CardTitle>
        <CardDescription>
          New to PAP Console? Follow these steps to set up your access control
          policies
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-3">
          {stepsWithStatus.map((step) => (
            <StepCard key={step.number} step={step} />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
